import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography, Divider } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "20px",
    textAlign: "left",
    minHeight: "400px",
    overflowY: "scroll",
    backgroundColor: "#e0e0e0",
  },
  nivelModalidad: {
    fontWeight: "bold",
    marginTop: 16,
  },
  nivelRama: {
    marginLeft: 20,
    marginTop: 6,
    fontWeight: "bold",
    color: "#555",
  },
  nivelCategoria: {
    marginLeft: 40,
    marginTop: 4,
  },
  nivelOrigen: {
    marginLeft: 60,
    color: "#777",
  },
}));

function nombreRama(rama) {
  if (rama === "V") return "Varonil";
  if (rama === "F") return "Femenil";
  return "Mixta";
}

function resolveOrigen(origen, eventSubEventsRegistry) {
  for (var modKey in eventSubEventsRegistry || {}) {
    const modalidadReg = eventSubEventsRegistry[modKey];
    if (modalidadReg.codigoModalidad !== origen.modalidad) continue;
    const ramaKey = "rama_" + origen.genero;
    const ramaObj = modalidadReg.ramaCat && modalidadReg.ramaCat[ramaKey];
    if (!ramaObj) continue;
    for (var catKey in ramaObj.categorias || {}) {
      const categoriaReg = ramaObj.categorias[catKey];
      if (categoriaReg.codigo === origen.categoria) {
        return { found: true, modalidadReg, ramaObj, categoriaReg };
      }
    }
  }
  return { found: false };
}

function buildUsedOriginKeySet(eventSubEvents) {
  const usedKeys = new Set();
  for (var modKey in eventSubEvents || {}) {
    const modalidad = eventSubEvents[modKey];
    for (var ramaKey in modalidad.ramaCat || {}) {
      const ramaObj = modalidad.ramaCat[ramaKey];
      for (var catKey in ramaObj.categorias || {}) {
        const categoria = ramaObj.categorias[catKey];
        for (var i = 0; i < (categoria.origenesInscripcion || []).length; i++) {
          const origen = categoria.origenesInscripcion[i];
          usedKeys.add(origen.modalidad + "|" + origen.genero + "|" + origen.categoria);
        }
      }
    }
  }
  return usedKeys;
}

function buildUnusedRegistryEntries(eventSubEventsRegistry, usedKeys) {
  const unused = [];
  for (var modKey in eventSubEventsRegistry || {}) {
    const modalidadReg = eventSubEventsRegistry[modKey];
    for (var ramaKey in modalidadReg.ramaCat || {}) {
      const ramaObj = modalidadReg.ramaCat[ramaKey];
      for (var catKey in ramaObj.categorias || {}) {
        const categoriaReg = ramaObj.categorias[catKey];
        const identidad =
          modalidadReg.codigoModalidad + "|" + ramaObj.rama + "|" + categoriaReg.codigo;
        if (!usedKeys.has(identidad)) {
          unused.push({ modKey, modalidadReg, ramaKey, ramaObj, catKey, categoriaReg });
        }
      }
    }
  }
  return unused;
}

export default function ModalidadesTreeView() {
  const classes = useStyles();
  const eventSubEvents = useSelector((state) => state.events.eventSubEvents);
  const eventSubEventsRegistry = useSelector(
    (state) => state.events.eventSubEventsRegistry
  );

  const unusedEntries = useMemo(() => {
    if (!eventSubEvents || !eventSubEventsRegistry) return [];
    const usedKeys = buildUsedOriginKeySet(eventSubEvents);
    return buildUnusedRegistryEntries(eventSubEventsRegistry, usedKeys);
  }, [eventSubEvents, eventSubEventsRegistry]);

  if (!eventSubEvents || !eventSubEventsRegistry) {
    return (
      <Paper className={classes.paper}>
        <Typography>Cargando modalidades...</Typography>
      </Paper>
    );
  }

  const timingKeys = Object.keys(eventSubEvents);

  if (timingKeys.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Typography>No hay modalidades de crono configuradas.</Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography gutterBottom variant={"h4"} component="h2">
        Árbol de modalidades (inscripción → crono)
      </Typography>
      <Typography gutterBottom variant={"body2"}>
        Cada modalidad de crono muestra sus ramas, categorías, y de qué
        modalidad(es)/categoría(s) de inscripción provienen.
      </Typography>

      {timingKeys.map((modKey) => {
        const modalidad = eventSubEvents[modKey];
        const ramas = modalidad.ramaCat || {};
        return (
          <div key={modKey}>
            <Typography variant="h6" className={classes.nivelModalidad}>
              {modalidad.descripcionModalidad} ({modalidad.modalidad} km)
            </Typography>
            {Object.keys(ramas).map((ramaKey) => {
              const ramaObj = ramas[ramaKey];
              const categorias = ramaObj.categorias || {};
              return (
                <div key={ramaKey}>
                  <Typography variant="subtitle1" className={classes.nivelRama}>
                    {nombreRama(ramaObj.rama)}
                  </Typography>
                  {Object.keys(categorias).map((catKey) => {
                    const categoria = categorias[catKey];
                    const origenes = categoria.origenesInscripcion || [];
                    return (
                      <div key={catKey}>
                        <Typography variant="body1" className={classes.nivelCategoria}>
                          {categoria.nombreCategoria} (Ganadores:{" "}
                          {categoria.cantidadGanadores ?? 0})
                        </Typography>
                        {origenes.length === 0 && (
                          <Typography variant="body2" className={classes.nivelOrigen}>
                            Sin origen de inscripción registrado
                          </Typography>
                        )}
                        {origenes.map((origen, i) => {
                          const resolved = resolveOrigen(origen, eventSubEventsRegistry);
                          const label = resolved.found
                            ? "← " +
                              resolved.modalidadReg.descripcionModalidad +
                              " / " +
                              nombreRama(resolved.ramaObj.rama) +
                              " / " +
                              resolved.categoriaReg.nombreCategoria
                            : "← (origen no encontrado: modalidad " +
                              origen.modalidad +
                              ", " +
                              origen.genero +
                              ", categoría " +
                              origen.categoria +
                              ")";
                          return (
                            <Typography
                              key={i}
                              variant="body2"
                              className={classes.nivelOrigen}
                            >
                              {label}
                            </Typography>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}

      <Divider style={{ margin: "20px 0" }} />

      <Typography variant="h6" className={classes.nivelModalidad}>
        Sin usar en cronometraje ({unusedEntries.length})
      </Typography>
      {unusedEntries.length === 0 && (
        <Typography variant="body1" className={classes.nivelCategoria}>
          Todas las categorías de inscripción están mapeadas a crono
        </Typography>
      )}
      {unusedEntries.map((entry, i) => (
        <Typography key={i} variant="body1" className={classes.nivelCategoria}>
          {entry.modalidadReg.descripcionModalidad} /{" "}
          {nombreRama(entry.ramaObj.rama)} / {entry.categoriaReg.nombreCategoria}
        </Typography>
      ))}
    </Paper>
  );
}
